import type { SalaryPaymentLineDto } from '@bbl/validation';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Download,
  ExternalLink,
  FileX2,
  MoreHorizontal,
  RefreshCw,
  UserCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError, fetchApiFile, openApiDocument } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { SalaryStatusBadge } from '../components/finance/StatusBadge';
import { formatDate, formatINR } from '../lib/format';
import {
  cycleLabel,
  type SalaryCycle,
  type SalaryPayment,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/finance/payslips/$paymentId')({
  component: PayslipDetailPage,
});


const ADMIN_ROLES = ['super_admin', 'finance_admin'] as const;
const SUPER_ADMIN_ROLE = 'super_admin';

// ---------------------------------------------------------------------------
// Wire shape — the backend agent enriches GET /api/salaries/payments/:id with
// `approverName`, `payeeUser`, etc. Until that lands, every enrichment field
// is optional and we derive payee from `teacher`/`manager` (the shape served
// today: `{ payment, cycle, teacher, manager }`).
// ---------------------------------------------------------------------------

interface TeacherEmbed {
  id: string;
  name: string;
  userId: string | null;
  status?: string;
}

interface ManagerEmbed {
  id: string;
  name: string;
  userId: string | null;
  status?: string;
}

interface PayeeUserEmbed {
  id: string;
  displayName: string | null;
  email: string | null;
}

interface PayeeEmbed {
  id: string;
  name: string;
  role: 'teacher' | 'manager';
  userId: string | null;
}

interface SalaryPaymentDetail {
  payment: SalaryPayment;
  cycle: SalaryCycle;
  teacher?: TeacherEmbed | null;
  manager?: ManagerEmbed | null;
  payee?: PayeeEmbed | null;
  payeeUser?: PayeeUserEmbed | null;
  approverName?: string | null;
  /** Additions such as the reporting incentive. */
  lines?: SalaryPaymentLineDto[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PAID_VIA_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  upi_offline: 'UPI (offline)',
  cheque: 'Cheque',
  other: 'Other',
};

const formatPaidVia = (via: string | null | undefined): string => {
  if (!via) return '—';
  return PAID_VIA_LABELS[via] ?? via;
};

// Asks for the payslip's link; if the PDF isn't there yet, the server
// starts making it and answers "still being prepared".
const payslipLinkPath = (paymentId: string): string =>
  `/api/salaries/payments/${encodeURIComponent(paymentId)}/payslip-url`;

const derivePayee = (data: SalaryPaymentDetail): PayeeEmbed | null => {
  if (data.payee) return data.payee;
  if (data.payment.role === 'teacher' && data.teacher) {
    return {
      id: data.teacher.id,
      name: data.teacher.name,
      role: 'teacher',
      userId: data.teacher.userId,
    };
  }
  if (data.payment.role === 'manager' && data.manager) {
    return {
      id: data.manager.id,
      name: data.manager.name,
      role: 'manager',
      userId: data.manager.userId,
    };
  }
  return null;
};

const roleLabel = (role: 'teacher' | 'manager'): string =>
  role === 'teacher' ? 'Teacher' : 'Nalim';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function PayslipDetailPage(): JSX.Element {
  const { paymentId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isAdmin = role !== null && (ADMIN_ROLES as readonly string[]).includes(role);
  const isSuperAdmin = role === SUPER_ADMIN_ROLE;

  const [overflowOpen, setOverflowOpen] = useState(false);
  const [pdfBlocked, setPdfBlocked] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['salary-payslip', 'detail', paymentId] as const,
    queryFn: ({ signal }) =>
      apiRequest<SalaryPaymentDetail>(
        `/api/salaries/payments/${encodeURIComponent(paymentId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const data = detailQuery.data;
  const payment = data?.payment;
  const cycle = data?.cycle;
  const payee = useMemo(() => (data ? derivePayee(data) : null), [data]);
  const isPaid = payment?.status === 'paid';
  const pdfPath = useMemo(() => payslipLinkPath(paymentId), [paymentId]);

  useEffect(() => {
    if (!overflowOpen) return;
    const handler = (e: MouseEvent): void => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest('[data-payslip-overflow]')) setOverflowOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [overflowOpen]);

  const openPdf = async (download: boolean): Promise<void> => {
    if (!isPaid) {
      toast('Payslip is only available after the payment is marked paid.', {
        variant: 'error',
      });
      return;
    }
    try {
      await openApiDocument(pdfPath, { download });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not open the payslip.', { variant: 'error' });
    }
  };
  const handleOpenPdf = (): void => void openPdf(false);

  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast('Link copied.', { variant: 'success' });
    } catch {
      toast('Could not copy link.', { variant: 'error' });
    }
    setOverflowOpen(false);
  };

  // Backend agent (slice B contract) exposes
  // POST /api/salaries/payments/:id/payslip/regenerate (super_admin only).
  // Regeneration replaces the rendered PDF in R2; the payslip number is
  // preserved (immutable artefact identifier).
  const regenerateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/salaries/payments/${encodeURIComponent(paymentId)}/payslip/regenerate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Payslip regeneration started.', { variant: 'success' });
      void queryClient.invalidateQueries({
        queryKey: ['salary-payslip', 'detail', paymentId],
      });
      // The new PDF is made in the background; fetch the preview again
      // once it's had a few seconds.
      setPdfBlocked(false);
      window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['salary-payslip', 'pdf'] }), 5000);
      setOverflowOpen(false);
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not regenerate payslip.', {
        variant: 'error',
      });
    },
  });

  const handleRegenerate = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Regenerate the payslip PDF?',
      description:
        'A new PDF will replace the existing one. The payslip number is preserved. Only super admins can perform this action.',
      confirmLabel: 'Regenerate',
    });
    if (!ok) return;
    regenerateMutation.mutate();
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!payment || !cycle) return [];
    return [
      { label: 'Net paid', value: formatINR(payment.netPaise) },
      {
        label: 'Paid on',
        value: payment.paidAt
          ? formatDate(payment.paidAt, { withTime: true })
          : '—',
      },
      {
        label: 'Payslip #',
        value: payment.payslipNumber ? (
          <span className="font-mono text-base">{payment.payslipNumber}</span>
        ) : (
          '—'
        ),
      },
      { label: 'Cycle', value: cycleLabel(cycle.year, cycle.month) },
    ];
  }, [payment, cycle]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Payslip"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !data || !payment || !cycle ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={`Payslip ${payment.payslipNumber ?? '—'}`}
              sublabel={
                <>
                  {payee ? (
                    <span className="font-medium text-[color:var(--color-text)]">
                      {payee.name}
                    </span>
                  ) : (
                    <span>Unknown payee</span>
                  )}
                  <span aria-hidden="true">·</span>
                  <span>{payee ? roleLabel(payee.role) : roleLabel(payment.role)}</span>
                  <span aria-hidden="true">·</span>
                  <span className="tabular-nums">
                    {cycleLabel(cycle.year, cycle.month)}
                  </span>
                </>
              }
              statusChip={<SalaryStatusBadge status={payment.status} />}
              backTo="/finance/receipts"
              actions={
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<ExternalLink size={14} aria-hidden="true" />}
                    onClick={handleOpenPdf}
                    disabled={!isPaid}
                  >
                    Open PDF
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Download size={14} aria-hidden="true" />}
                    onClick={() => void openPdf(true)}
                    disabled={!isPaid}
                  >
                    Download
                  </Button>
                  {isSuperAdmin ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<RefreshCw size={14} aria-hidden="true" />}
                      loading={regenerateMutation.isPending}
                      onClick={() => {
                        void handleRegenerate();
                      }}
                      disabled={!isPaid}
                    >
                      Regenerate PDF
                    </Button>
                  ) : null}
                  <PayslipOverflowMenu
                    open={overflowOpen}
                    onOpenChange={setOverflowOpen}
                    paymentId={payment.id}
                    onCopyLink={() => {
                      void handleCopyLink();
                    }}
                    showViewSalaryPayment={isAdmin}
                  />
                </>
              }
            />

            <KpiStrip items={kpiItems} />

            <div className="space-y-4">
              <PdfPreviewSection
                pdfPath={pdfPath}
                isPaid={isPaid}
                blocked={pdfBlocked}
                onMarkBlocked={() => setPdfBlocked(true)}
                onOpen={handleOpenPdf}
                regenerating={regenerateMutation.isPending}
                cacheBust={data.payment.updatedAt}
              />

              <PayBreakdownSection payment={payment} lines={data?.lines ?? []} />

              <div className="grid gap-4 lg:grid-cols-2">
                <PayeeSection
                  payee={payee}
                  payeeUser={data.payeeUser ?? null}
                />
                <CycleSection cycle={cycle} />
              </div>

              <ApprovalSection
                payment={payment}
                approverName={data.approverName ?? null}
              />
            </div>

            <ActivityPanel
              targetType="salary_payment"
              targetId={payment.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overflow menu — Copy link + View salary payment (admins only).
// View salary payment uses <a href> rather than <Link to=...> because the
// target route /finance/salaries/payments/$paymentId is being built in
// parallel by slice A and is not yet present in routeTree.gen.ts. Once that
// slice merges, this can be promoted to a typed Link.
// ---------------------------------------------------------------------------

interface PayslipOverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  paymentId: string;
  onCopyLink: () => void;
  showViewSalaryPayment: boolean;
}

function PayslipOverflowMenu({
  open,
  onOpenChange,
  paymentId,
  onCopyLink,
  showViewSalaryPayment,
}: PayslipOverflowMenuProps): JSX.Element {
  return (
    <div className="relative" data-payslip-overflow>
      <Button
        variant="ghost"
        size="sm"
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        More
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-9 z-10 min-w-[220px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          <button
            role="menuitem"
            type="button"
            onClick={onCopyLink}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-text)] hover:bg-[color:var(--color-surface)] focus:outline-none"
          >
            <Copy size={14} aria-hidden="true" />
            Copy link
          </button>
          {showViewSalaryPayment ? (
            <a
              role="menuitem"
              href={`/finance/salaries/payments/${encodeURIComponent(paymentId)}`}
              onClick={() => onOpenChange(false)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-text)] no-underline hover:bg-[color:var(--color-surface)] focus:outline-none"
            >
              <ExternalLink size={14} aria-hidden="true" />
              View salary payment
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  children: ReactNode;
  className?: string;
  action?: ReactNode;
}

function Section({ title, children, className, action }: SectionProps): JSX.Element {
  return (
    <section
      className={clsx(
        'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          {title}
        </h3>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="mt-3 text-sm text-[color:var(--color-text)]">{children}</div>
    </section>
  );
}

interface FieldRowProps {
  label: string;
  value: ReactNode;
  highlight?: boolean;
}

function FieldRow({ label, value, highlight = false }: FieldRowProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd
        className={clsx(
          'min-w-0 text-right text-sm',
          highlight
            ? 'font-semibold text-[color:var(--color-text)]'
            : 'text-[color:var(--color-text)]',
        )}
      >
        {value ?? (
          <span className="text-[color:var(--color-text-muted)]">—</span>
        )}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF preview — iframe pointed at the payslip-file endpoint with graceful
// fallback when the file is unavailable (not yet generated / network blocked
// / cross-origin auth refused).
// ---------------------------------------------------------------------------

interface PdfPreviewSectionProps {
  pdfPath: string;
  isPaid: boolean;
  blocked: boolean;
  onMarkBlocked: () => void;
  onOpen: () => void;
  regenerating: boolean;
  cacheBust: string;
}

function PdfPreviewSection({
  pdfPath,
  isPaid,
  blocked,
  onMarkBlocked,
  onOpen,
  regenerating,
  cacheBust,
}: PdfPreviewSectionProps): JSX.Element {
  // Fetched with the same sign-in as the rest of the page and shown from
  // memory: pointing the frame at the API would need a sign-in cookie for
  // the API's site, which Safari and iPhones don't keep. Keyed on the
  // payment's updatedAt so a regenerated PDF is fetched again.
  const fileQuery = useQuery({
    queryKey: ['salary-payslip', 'pdf', pdfPath, cacheBust] as const,
    queryFn: async ({ signal }) => {
      const link = await apiRequest<{ url?: string }>(pdfPath, { signal });
      if (!link?.url) throw new ApiError(202, 'document_pending', 'The payslip is still being prepared.');
      return fetchApiFile(link.url, signal);
    },
    enabled: isPaid && !regenerating,
    // Just marked paid: the PDF takes a few seconds to make.
    retry: (n, err) => err instanceof ApiError && ['document_pending', 'payslip_missing'].includes(err.code) && n < 6,
    retryDelay: 3000,
    staleTime: Infinity,
  });
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!fileQuery.data) return;
    const url = URL.createObjectURL(fileQuery.data.blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [fileQuery.data]);
  useEffect(() => {
    if (fileQuery.isError) onMarkBlocked();
  }, [fileQuery.isError, onMarkBlocked]);
  const iframeSrc = objectUrl ? `${objectUrl}#view=FitH&zoom=page-width` : undefined;

  return (
    <Section
      title="Payslip PDF"
      action={
        isPaid ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<ExternalLink size={14} aria-hidden="true" />}
            onClick={onOpen}
          >
            Open in new tab
          </Button>
        ) : null
      }
    >
      {!isPaid ? (
        <div className="rounded-md border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-6 text-center">
          <FileX2
            size={28}
            aria-hidden="true"
            className="mx-auto text-[color:var(--color-text-muted)]"
          />
          <p className="mt-2 text-sm font-medium text-[color:var(--color-text)]">
            Payslip not generated yet
          </p>
          <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
            The PDF is created automatically once the salary payment is marked
            paid.
          </p>
        </div>
      ) : blocked || regenerating ? (
        <div className="rounded-md border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-6 text-center">
          <FileX2
            size={28}
            aria-hidden="true"
            className="mx-auto text-[color:var(--color-text-muted)]"
          />
          <p className="mt-2 text-sm font-medium text-[color:var(--color-text)]">
            {regenerating ? 'Regenerating PDF…' : 'In-page preview unavailable'}
          </p>
          <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
            {regenerating
              ? 'The new payslip will be ready in a few seconds.'
              : 'The payslip couldn’t be shown here. Use Open PDF or Download to view it.'}
          </p>
          {!regenerating ? (
            <div className="mt-3">
              <Button
                size="sm"
                variant="secondary"
                icon={<ExternalLink size={14} aria-hidden="true" />}
                onClick={onOpen}
              >
                Open in new tab
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
          {iframeSrc ? (
            <iframe title="Payslip PDF preview" src={iframeSrc} className="block h-[640px] w-full" />
          ) : (
            <p className="p-6 text-center text-sm text-[color:var(--color-text-muted)]">Loading the payslip…</p>
          )}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Pay breakdown — gross / deductions / net
// ---------------------------------------------------------------------------

function PayBreakdownSection({
  payment,
  lines,
}: {
  payment: SalaryPayment;
  lines: SalaryPaymentLineDto[];
}): JSX.Element {
  return (
    <Section title="Pay breakdown">
      <dl className="space-y-2">
        <FieldRow label="Gross" value={formatINR(payment.grossPaise)} />
        {lines.map((l) => (
          <FieldRow key={l.id} label={l.label} value={`+ ${formatINR(l.amountPaise)}`} />
        ))}
        <FieldRow
          label="Deductions"
          value={
            payment.deductionsPaise > 0
              ? `− ${formatINR(payment.deductionsPaise)}`
              : '—'
          }
        />
        <div className="my-2 border-t border-[color:var(--color-border)]" />
        <FieldRow
          label="Net paid"
          value={formatINR(payment.netPaise)}
          highlight
        />
      </dl>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Payee card — name + role + linked user with View login link
// ---------------------------------------------------------------------------

interface PayeeSectionProps {
  payee: PayeeEmbed | null;
  payeeUser: PayeeUserEmbed | null;
}

function PayeeSection({ payee, payeeUser }: PayeeSectionProps): JSX.Element {
  if (!payee) {
    return (
      <Section title="Payee">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Payee details unavailable.
        </p>
      </Section>
    );
  }
  return (
    <Section title="Payee">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]">
          <UserCircle size={24} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          {payee.role === 'teacher' ? (
            <Link
              to="/people/teachers/$teacherId"
              params={{ teacherId: payee.id }}
              className="text-sm font-medium text-[color:var(--color-text)] hover:underline"
            >
              {payee.name}
            </Link>
          ) : (
            <Link
              to="/people/managers/$managerId"
              params={{ managerId: payee.id }}
              className="text-sm font-medium text-[color:var(--color-text)] hover:underline"
            >
              {payee.name}
            </Link>
          )}
          <p className="text-xs text-[color:var(--color-text-muted)]">
            {roleLabel(payee.role)}
          </p>
          {payeeUser ? (
            <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
              {payeeUser.email ?? payeeUser.displayName ?? '—'}
            </p>
          ) : null}
          {payee.userId ? (
            <div className="mt-2">
              <Link
                to="/system/users/$userId"
                params={{ userId: payee.userId }}
                className="text-xs text-gold-700 underline hover:no-underline"
              >
                View login
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Cycle card — link to /finance/salaries/$cycleId (slice A route — uses
// <a href> until typed route lands).
// ---------------------------------------------------------------------------

function CycleSection({ cycle }: { cycle: SalaryCycle }): JSX.Element {
  return (
    <Section title="Salary cycle">
      <p className="text-sm font-medium text-[color:var(--color-text)]">
        {cycleLabel(cycle.year, cycle.month)}
      </p>
      <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
        Payout {formatDate(`${cycle.payoutDate}T00:00:00Z`)}
      </p>
      {cycle.notes ? (
        <p className="mt-2 text-sm text-[color:var(--color-text)] whitespace-pre-wrap">
          {cycle.notes}
        </p>
      ) : null}
      <div className="mt-3">
        <a
          href={`/finance/salaries/${encodeURIComponent(cycle.id)}`}
          className="text-xs text-gold-700 underline hover:no-underline"
        >
          Open cycle
        </a>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Approval card — approver + paidAt + paidVia + reference
// ---------------------------------------------------------------------------

interface ApprovalSectionProps {
  payment: SalaryPayment;
  approverName: string | null;
}

function ApprovalSection({
  payment,
  approverName,
}: ApprovalSectionProps): JSX.Element {
  const fallbackApprover = payment.approvedBy ? `User ${payment.approvedBy}` : null;
  return (
    <Section title="Approval">
      <dl className="space-y-2">
        <FieldRow
          label="Approved by"
          value={approverName ?? fallbackApprover ?? null}
        />
        <FieldRow
          label="Paid at"
          value={
            payment.paidAt
              ? formatDate(payment.paidAt, { withTime: true })
              : null
          }
        />
        <FieldRow label="Paid via" value={formatPaidVia(payment.paidVia)} />
        <FieldRow
          label="Reference"
          value={
            payment.paidReference ? (
              <span className="font-mono text-xs">{payment.paidReference}</span>
            ) : null
          }
        />
      </dl>
    </Section>
  );
}
